/**
 * <s-smooth-scroll> gives mouse-wheel page scrolling a soft, eased glide.
 *
 * - Only smooths the page itself; wheel events inside scrollable regions
 *   (drawers, dialogs, menus, carousels) keep native behavior.
 * - Skipped entirely for reduced-motion visitors and touch/trackpad-less
 *   coarse pointers, where native scrolling already feels right.
 * - Ctrl/meta wheel (zoom) and horizontal wheel are left alone.
 */
const EASE = 0.1; // share of the remaining distance covered each frame
const STOP_THRESHOLD = 0.5;

class SmoothScroll extends HTMLElement {
  #target = 0;
  #current = 0;
  #frame = 0;
  #onWheel = (event) => this.#handleWheel(event);
  #onSync = () => this.#sync();

  connectedCallback() {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (reduced || !fine) return;

    this.#sync();
    window.addEventListener('wheel', this.#onWheel, { passive: false });
    window.addEventListener('scroll', this.#onSync, { passive: true });
    window.addEventListener('resize', this.#onSync, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('wheel', this.#onWheel);
    window.removeEventListener('scroll', this.#onSync);
    window.removeEventListener('resize', this.#onSync);
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  #maxScroll() {
    return document.documentElement.scrollHeight - window.innerHeight;
  }

  /** Keep in step with scrolling we didn't cause (keyboard, anchors, scrollbar). */
  #sync() {
    if (this.#frame) return;
    this.#current = this.#target = window.scrollY;
  }

  #isInsideScrollable(node) {
    let el = node instanceof Element ? node : null;
    while (el && el !== document.body && el !== document.documentElement) {
      if (el.matches('dialog[open], [popover]')) return true;
      const style = getComputedStyle(el);
      const overflowY = style.overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) return true;
      el = el.parentElement;
    }
    return false;
  }

  #handleWheel(event) {
    if (event.defaultPrevented || event.ctrlKey || event.metaKey) return;
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
    if (document.querySelector('dialog[open]')) return;
    if (this.#isInsideScrollable(event.target)) return;

    let delta = event.deltaY;
    if (event.deltaMode === 1) delta *= 16;
    else if (event.deltaMode === 2) delta *= window.innerHeight;

    event.preventDefault();
    if (!this.#frame) this.#current = window.scrollY;
    this.#target = Math.max(0, Math.min(this.#target + delta, this.#maxScroll()));
    if (!this.#frame) this.#frame = requestAnimationFrame(() => this.#tick());
  }

  #tick() {
    this.#current += (this.#target - this.#current) * EASE;
    if (Math.abs(this.#target - this.#current) < STOP_THRESHOLD) this.#current = this.#target;
    window.scrollTo({ top: this.#current, behavior: 'instant' });

    if (this.#current !== this.#target) {
      this.#frame = requestAnimationFrame(() => this.#tick());
    } else {
      this.#frame = 0;
    }
  }
}

if (!customElements.get('s-smooth-scroll')) {
  customElements.define('s-smooth-scroll', SmoothScroll);
}
